import { PopoverButton } from '@headlessui/react';
import { Link, useFetcher } from '@remix-run/react';
import { GQLCartFragment, GQLOperationalSettingsFragment } from '@theprintbar/graphql';
import { WebRoutes, cn, getCartPrice, getCartTurnaround, getCurrencyString, getTurnaroundString } from '@theprintbar/shared';
import { ButtonSize, CartIcon, LightningBolt, PrimaryLinkButton } from '@theprintbar/ui';

interface CartCheckoutProps {
	settings: GQLOperationalSettingsFragment;
	cart: GQLCartFragment;
	className?: string;
	hideButtons?: boolean;
	isCart?: boolean;
	isCheckout?: boolean;
	quoteSession?: any;
}

export function CartCheckout({ settings, cart, className, isCart, isCheckout, quoteSession, hideButtons }: CartCheckoutProps) {
	const fetcher = useFetcher();
	const { total, shipping, items, couponDiscount } = getCartPrice(settings, cart);
	const turnaround = getCartTurnaround(settings, cart);

	return (
		<>
			{!isCheckout && (
				<div className="flex justify-between mb-2 text-sm">
					<h5 className="text-xs uppercase text-blue">Expected turnaround</h5>
					<span className="text-blue">
						{turnaround === 1 && <LightningBolt className="inline w-4 h-4 mr-0.5" />}
						{getTurnaroundString(turnaround, false)}
					</span>
				</div>
			)}
			<div className={cn('py-4 border-t border-blue', className)}>
				<dl className="space-y-4 text-sm font-medium text-gray-500">
					<div className="space-y-2">
						<div className="flex justify-between">
							<dt>
								<h5 className="text-xs uppercase text-blue">Subtotal</h5>
							</dt>
							<dd className="text-blue">{getCurrencyString(items)}</dd>
						</div>
						<div className="flex justify-between text-blue ">
							<dt>
								<h5 className="uppercase text-xs">Shipping</h5>
							</dt>
							<dd className={cn(!!!shipping && 'text-sm')}>
								{
									// If shipping is free, show free shipping
									cart.shippingMethod === 'pickup'
										? 'Free Pickup'
										: !!cart.shippingMethod && !shipping
										? 'Free shipping'
										: !!cart.shippingMethod
										? getCurrencyString(shipping)
										: // If shipping is not free, show calculated at next step
										  'Calculated at next step'
								}
							</dd>
						</div>
						{!!couponDiscount?.discount && (
							<div className="flex justify-between">
								<dt>
									<h5 className="text-xs uppercase text-blue">{couponDiscount.description}</h5>
									<button
										className="font-normal text-xs underline text-gray-800 flex items-center"
										onClick={() => {
											fetcher.submit(
												{},
												{
													method: 'POST',
													action: (quoteSession ? WebRoutes.API.commerce.quote : WebRoutes.API.commerce.cart)
														.coupon.remove,
												},
											);
										}}
									>
										{couponDiscount.code} - Remove
									</button>
								</dt>
								<dd className="text-blue">{getCurrencyString(-couponDiscount.discount)}</dd>
							</div>
						)}
					</div>

					<div className="flex items-center justify-between border-t border-blue pt-4 text-gray-900">
						<dt>
							<h4 className="text-lg text-blue">Total</h4>
						</dt>
						<dd className="flex items-center text-blue text-lg space-x-1">
							<span className="text-xs">AUD</span>{' '}
							{couponDiscount?.discount ? (
								<p className="line-through text-grey/60">{getCurrencyString(total + couponDiscount.discount)}</p>
							) : (
								''
							)}
							<p>{getCurrencyString(total)}</p>
						</dd>
					</div>

					{!hideButtons && (
						<div className="flex justify-between items-center mt-4">
							{!isCart && (
								<PopoverButton as={Link} reloadDocument to={WebRoutes.Cart}>
									<p className="text-xs text-blue underline">View full {quoteSession ? 'quote' : 'cart'}</p>
								</PopoverButton>
							)}
							<PrimaryLinkButton
								to={WebRoutes.Checkout.Information}
								icon={CartIcon}
								size={isCart ? ButtonSize.L : ButtonSize.M}
								className="w-full md:w-auto min-w-52 ml-auto"
							>
								Checkout
							</PrimaryLinkButton>
						</div>
					)}
				</dl>
			</div>
		</>
	);
}
